import { Disclosure, DisclosureContent, DisclosureProvider } from '@ariakit/react'
import styled from 'styled-components'

import { Carat } from '@syconium/little-miss-figgy'

import { useLocalization } from '../../../../app/_providers/LocalizationProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { useEmbroidery } from '../../../../containers/EmbroideryContainer/EmbroideryProvider'
import { formatPrettyCurrency } from '../../../../lib/utils/currency'

export const EmbroideryPriceSummary: React.FC<{
  selectedVariantPrice: { amount: number; currencyCode: string }
}> = ({ selectedVariantPrice }) => {
  const { locale } = useLocalization()
  const {
    embroidery: {
      modal: {
        priceSummary: { label, lineItems, finalSaleLabel, totalLabel },
      },
    },
  } = useTranslation()
  const {
    embroiderySelectionsEnabledForBag,
    isEmbroideryTypeSelectedForBag,
    productEmbroideryOptionKeys,
    embroideryPriceRangeByType,
  } = useEmbroidery()

  const totalPriceInCents =
    selectedVariantPrice.amount +
    (embroiderySelectionsEnabledForBag.embroideryItemVariant?.productInfo.price.amount ?? 0) +
    (embroiderySelectionsEnabledForBag.embroiderySetupVariant?.productInfo.price.amount ?? 0)

  const getFormattedPrice = (price: number) =>
    formatPrettyCurrency({
      unit: 'dollars',
      value: price,
      currency: selectedVariantPrice.currencyCode,
      locale: locale,
    })

  const formattedTotalPrice = getFormattedPrice(totalPriceInCents)
  const formattedProductPrice = getFormattedPrice(selectedVariantPrice.amount)

  return (
    <DisclosureProvider>
      <Container>
        <Disclosure render={htmlProps => <DisclosureHeader {...htmlProps} />}>
          <Label>
            <DisclosureCaratIcon />
            <h2>{label}</h2>
          </Label>
          <PriceTotal>{formattedTotalPrice}</PriceTotal>
        </Disclosure>
        <DisclosureContent>
          <LineItems>
            <LineItem>
              <p>{lineItems['PRODUCT']}</p>
              {formattedProductPrice ? <p>{formattedProductPrice}</p> : null}
            </LineItem>
            {productEmbroideryOptionKeys.map(embroideryType => {
              const priceRange = embroideryPriceRangeByType[embroideryType]
              const isSelectedForAddToBag = isEmbroideryTypeSelectedForBag(embroideryType)
              if (!isSelectedForAddToBag) return null
              // This is an itemized list. So we use priceRange.min instead of priceRange.effectivePrice because effective price will include addons like digitization, but we are showing those split off from this.
              return (
                <LineItem key={embroideryType}>
                  <p>{lineItems[embroideryType]}</p>
                  <p>{priceRange?.min.displayValue}</p>
                </LineItem>
              )
            })}
            {embroiderySelectionsEnabledForBag.embroiderySetupVariant?.productInfo.price.amount ? (
              <LineItem>
                <p>{lineItems['DIGITIZATION']}</p>
                <p>
                  {getFormattedPrice(
                    // TODO: Fix this auto-generated suppression.
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                    embroiderySelectionsEnabledForBag.embroiderySetupVariant?.productInfo.price
                      .amount
                  )}
                </p>
              </LineItem>
            ) : null}
          </LineItems>
          <StyledHr />
          <FinalSale>{finalSaleLabel}</FinalSale>
          <Total>
            <Label>{totalLabel}</Label>
            <PriceTotal>{formattedTotalPrice}</PriceTotal>
          </Total>
        </DisclosureContent>
      </Container>
    </DisclosureProvider>
  )
}

const Container = styled.div`
  margin-top: ${o => o.theme.spacing(5)};
  border-radius: ${o => o.theme.spacing(1)};
  background-color: ${o => o.theme.color.fill.subtle};
  font-size: ${o => o.theme.spacing(3)};
  padding: ${o => o.theme.spacing(4)};
`

const DisclosureCaratIcon = styled(Carat)`
  padding: ${o => o.theme.spacing(1.5)};
  transform: rotate(90deg);
`

const DisclosureHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: ${o => o.theme.spacing(1)};
  font-weight: ${o => o.theme.typography.font.weight[700]};
  cursor: pointer;
  line-height: ${o => o.theme.spacing(6)};

  &[aria-expanded='true'] {
    ${DisclosureCaratIcon} {
      transform: rotate(-90deg);
    }
  }
`
const Label = styled.div`
  display: flex;
  align-items: center;
`

const PriceTotal = styled.div``

const LineItems = styled.div`
  font-weight: ${o => o.theme.typography.font.weight[600]};
`

const LineItem = styled.div`
  padding: ${o => o.theme.spacing(2)} 0;
  display: flex;
  justify-content: space-between;
`

const FinalSale = styled.div`
  padding: ${o => o.theme.spacing(2)} 0;
  font-size: ${o => o.theme.spacing(2)};
  font-weight: ${o => o.theme.typography.font.weight[600]};
  text-transform: uppercase;
  color: ${o => o.theme.color.text.critical.on.background};
`

const StyledHr = styled.hr`
  margin: ${o => o.theme.spacing(2)} 0;
  border-top: 0;
  border-color: ${o => o.theme.color.stroke.neutral.border.decoration.on.background};
`

const Total = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${o => o.theme.spacing(3)};
  font-weight: ${o => o.theme.typography.font.weight[600]};
`
